/**
 * Chunk Storage Service
 *
 * Uses IndexedDB to store audio chunks before upload.
 * Supports offline-first recording with background upload.
 */

const DB_NAME = 'DiktierwerkChunkDB';
const DB_VERSION = 1;
const CHUNKS_STORE = 'chunks';
const MANIFESTS_STORE = 'manifests';

/**
 * Initialize IndexedDB for chunk storage
 * @returns {Promise<IDBDatabase>}
 */
const openChunkDB = () => {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onerror = () => {
      console.error('ChunkDB error:', request.error);
      reject(request.error);
    };

    request.onsuccess = () => {
      resolve(request.result);
    };

    request.onupgradeneeded = (event) => {
      const db = event.target.result;

      // Create chunks store
      if (!db.objectStoreNames.contains(CHUNKS_STORE)) {
        const chunksStore = db.createObjectStore(CHUNKS_STORE, {
          keyPath: 'id',
          autoIncrement: true,
        });
        chunksStore.createIndex('recordingId', 'recordingId', { unique: false });
        chunksStore.createIndex('chunkIndex', 'chunkIndex', { unique: false });
        chunksStore.createIndex('status', 'status', { unique: false });
        chunksStore.createIndex('timestamp', 'timestamp', { unique: false });
        console.log('Chunks store created');
      }

      // Create manifests store
      if (!db.objectStoreNames.contains(MANIFESTS_STORE)) {
        const manifestsStore = db.createObjectStore(MANIFESTS_STORE, {
          keyPath: 'recordingId',
        });
        manifestsStore.createIndex('status', 'status', { unique: false });
        manifestsStore.createIndex('timestamp', 'timestamp', { unique: false });
        console.log('Manifests store created');
      }
    };
  });
};

// Chunk status constants
export const ChunkStatus = {
  PENDING: 'PENDING',
  UPLOADING: 'UPLOADING',
  UPLOADED: 'UPLOADED',
  FAILED: 'FAILED'
};

/**
 * Store a chunk in IndexedDB
 * @param {object} chunkData - Chunk data with blob, recordingId, chunkIndex, etc.
 * @returns {Promise<number>} ID of stored chunk
 */
export const storeChunk = async (chunkData) => {
  const db = await openChunkDB();
  const { recordingId, chunkIndex, blob, mimeType, duration, timestamp } = chunkData;

  // Convert blob to ArrayBuffer for storage
  const arrayBuffer = await blob.arrayBuffer();

  const record = {
    recordingId,
    chunkIndex,
    blob: arrayBuffer,
    blobType: mimeType,
    duration,
    timestamp: timestamp || Date.now(),
    status: ChunkStatus.PENDING,
    retryCount: 0,
    s3Key: null,
    uploadedAt: null
  };

  return new Promise((resolve, reject) => {
    const transaction = db.transaction([CHUNKS_STORE], 'readwrite');
    const store = transaction.objectStore(CHUNKS_STORE);
    const request = store.add(record);

    request.onsuccess = () => {
      console.log(`Chunk ${chunkIndex} stored for recording ${recordingId}`);
      resolve(request.result);
    };

    request.onerror = () => {
      console.error('Error storing chunk:', request.error);
      reject(request.error);
    };

    transaction.oncomplete = () => db.close();
  });
};

/**
 * Get all chunks for a recording
 * @param {string} recordingId - The recording ID
 * @returns {Promise<Array>} Array of chunks sorted by index
 */
export const getChunksForRecording = async (recordingId) => {
  const db = await openChunkDB();

  return new Promise((resolve, reject) => {
    const transaction = db.transaction([CHUNKS_STORE], 'readonly');
    const store = transaction.objectStore(CHUNKS_STORE);
    const index = store.index('recordingId');
    const request = index.getAll(recordingId);

    request.onsuccess = () => {
      const chunks = request.result.sort((a, b) => a.chunkIndex - b.chunkIndex);
      resolve(chunks);
    };

    request.onerror = () => {
      console.error('Error getting chunks:', request.error);
      reject(request.error);
    };

    transaction.oncomplete = () => db.close();
  });
};

/**
 * Get a chunk by ID and convert back to Blob
 * @param {number} id - Chunk record ID
 * @returns {Promise<object>} Chunk data with blob
 */
export const getChunkById = async (id) => {
  const db = await openChunkDB();

  return new Promise((resolve, reject) => {
    const transaction = db.transaction([CHUNKS_STORE], 'readonly');
    const store = transaction.objectStore(CHUNKS_STORE);
    const request = store.get(id);

    request.onsuccess = () => {
      const record = request.result;
      if (!record) {
        reject(new Error(`Chunk ${id} not found`));
        return;
      }

      // Convert ArrayBuffer back to Blob
      const blob = new Blob([record.blob], { type: record.blobType });
      resolve({
        ...record,
        blob,
        mimeType: record.blobType
      });
    };

    request.onerror = () => {
      console.error('Error getting chunk:', request.error);
      reject(request.error);
    };

    transaction.oncomplete = () => db.close();
  });
};

/**
 * Delete a chunk by ID
 * @param {number} id - Chunk record ID
 * @returns {Promise<void>}
 */
export const deleteChunkById = async (id) => {
  const db = await openChunkDB();

  return new Promise((resolve, reject) => {
    const transaction = db.transaction([CHUNKS_STORE], 'readwrite');
    const store = transaction.objectStore(CHUNKS_STORE);
    const request = store.delete(id);

    request.onsuccess = () => {
      console.log(`Chunk ${id} deleted`);
      resolve();
    };

    request.onerror = () => {
      console.error('Error deleting chunk:', request.error);
      reject(request.error);
    };

    transaction.oncomplete = () => db.close();
  });
};

/**
 * Update chunk status
 * @param {number} id - Chunk record ID
 * @param {string} status - New status
 * @param {object} additionalData - Additional data to merge (can include blob as Blob object)
 * @returns {Promise<void>}
 */
export const updateChunkStatus = async (id, status, additionalData = {}) => {
  const db = await openChunkDB();

  // Process blob conversion BEFORE transaction to avoid async issues in IndexedDB
  let processedData = { ...additionalData };
  if (additionalData.blob instanceof Blob) {
    try {
      processedData.blob = await additionalData.blob.arrayBuffer();
      // Use provided blobType or infer from blob
      processedData.blobType = additionalData.blobType || additionalData.blob.type;
    } catch (e) {
      console.error('Failed to convert blob to ArrayBuffer:', e);
    }
  }

  return new Promise((resolve, reject) => {
    const transaction = db.transaction([CHUNKS_STORE], 'readwrite');
    const store = transaction.objectStore(CHUNKS_STORE);
    const getRequest = store.get(id);

    getRequest.onsuccess = () => {
      const record = getRequest.result;
      if (!record) {
        reject(new Error(`Chunk ${id} not found`));
        return;
      }

      const updated = {
        ...record,
        status,
        ...processedData,
        updatedAt: Date.now()
      };

      if (status === ChunkStatus.FAILED) {
        updated.retryCount = (record.retryCount || 0) + 1;
      }

      const putRequest = store.put(updated);
      putRequest.onsuccess = () => resolve();
      putRequest.onerror = () => reject(putRequest.error);
    };

    getRequest.onerror = () => reject(getRequest.error);
    transaction.oncomplete = () => db.close();
  });
};

/**
 * Get pending chunks for upload
 * @returns {Promise<Array>} Chunks with PENDING status
 */
export const getPendingChunks = async () => {
  const db = await openChunkDB();

  return new Promise((resolve, reject) => {
    const transaction = db.transaction([CHUNKS_STORE], 'readonly');
    const store = transaction.objectStore(CHUNKS_STORE);
    const index = store.index('status');
    const request = index.getAll(ChunkStatus.PENDING);

    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
    transaction.oncomplete = () => db.close();
  });
};

/**
 * Store recording manifest
 * @param {object} manifest - Manifest data
 * @returns {Promise<string>} Recording ID
 */
export const storeManifest = async (manifest) => {
  const db = await openChunkDB();

  const record = {
    ...manifest,
    status: 'PENDING',
    createdAt: Date.now()
  };

  return new Promise((resolve, reject) => {
    const transaction = db.transaction([MANIFESTS_STORE], 'readwrite');
    const store = transaction.objectStore(MANIFESTS_STORE);
    const request = store.put(record);

    request.onsuccess = () => {
      console.log(`Manifest stored for recording ${manifest.recording_id}`);
      resolve(manifest.recording_id);
    };

    request.onerror = () => reject(request.error);
    transaction.oncomplete = () => db.close();
  });
};

/**
 * Get manifest by recording ID
 * @param {string} recordingId - Recording ID
 * @returns {Promise<object|null>} Manifest or null
 */
export const getManifest = async (recordingId) => {
  const db = await openChunkDB();

  return new Promise((resolve, reject) => {
    const transaction = db.transaction([MANIFESTS_STORE], 'readonly');
    const store = transaction.objectStore(MANIFESTS_STORE);
    const request = store.get(recordingId);

    request.onsuccess = () => resolve(request.result || null);
    request.onerror = () => reject(request.error);
    transaction.oncomplete = () => db.close();
  });
};

/**
 * Update manifest status
 * @param {string} recordingId - Recording ID
 * @param {string} status - New status
 * @returns {Promise<void>}
 */
export const updateManifestStatus = async (recordingId, status) => {
  const db = await openChunkDB();

  return new Promise((resolve, reject) => {
    const transaction = db.transaction([MANIFESTS_STORE], 'readwrite');
    const store = transaction.objectStore(MANIFESTS_STORE);
    const getRequest = store.get(recordingId);

    getRequest.onsuccess = () => {
      const record = getRequest.result;
      if (!record) {
        reject(new Error(`Manifest ${recordingId} not found`));
        return;
      }

      record.status = status;
      record.updatedAt = Date.now();

      const putRequest = store.put(record);
      putRequest.onsuccess = () => resolve();
      putRequest.onerror = () => reject(putRequest.error);
    };

    getRequest.onerror = () => reject(getRequest.error);
    transaction.oncomplete = () => db.close();
  });
};

/**
 * Delete all chunks and manifest for a recording
 * @param {string} recordingId - Recording ID
 * @returns {Promise<void>}
 */
export const deleteRecordingData = async (recordingId) => {
  const db = await openChunkDB();

  return new Promise((resolve, reject) => {
    const transaction = db.transaction([CHUNKS_STORE, MANIFESTS_STORE], 'readwrite');

    // Delete chunks
    const chunksStore = transaction.objectStore(CHUNKS_STORE);
    const chunksIndex = chunksStore.index('recordingId');
    const chunksRequest = chunksIndex.openCursor(recordingId);

    chunksRequest.onsuccess = (event) => {
      const cursor = event.target.result;
      if (cursor) {
        cursor.delete();
        cursor.continue();
      }
    };

    // Delete manifest
    const manifestsStore = transaction.objectStore(MANIFESTS_STORE);
    manifestsStore.delete(recordingId);

    transaction.oncomplete = () => {
      console.log(`Deleted all data for recording ${recordingId}`);
      db.close();
      resolve();
    };

    transaction.onerror = () => reject(transaction.error);
  });
};

/**
 * Get recording upload progress
 * @param {string} recordingId - Recording ID
 * @returns {Promise<object>} Progress info
 */
export const getUploadProgress = async (recordingId) => {
  const chunks = await getChunksForRecording(recordingId);
  const manifest = await getManifest(recordingId);

  const total = chunks.length;
  const uploaded = chunks.filter(c => c.status === ChunkStatus.UPLOADED).length;
  const failed = chunks.filter(c => c.status === ChunkStatus.FAILED).length;
  const pending = chunks.filter(c => c.status === ChunkStatus.PENDING).length;
  const uploading = chunks.filter(c => c.status === ChunkStatus.UPLOADING).length;

  return {
    recordingId,
    total,
    uploaded,
    failed,
    pending,
    uploading,
    percent: total > 0 ? Math.round((uploaded / total) * 100) : 0,
    isComplete: uploaded === total && total > 0,
    manifestUploaded: manifest?.status === 'UPLOADED'
  };
};
